import {
  BILLING_COUNTRIES,
  billingEmailLooksValid,
  vatLooksValid,
  type BillingCountry,
  type BillingDetails,
} from '@forecastify/shared';
import { Icon, Select, TextInput } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { Field } from '../card';
import { SettingsCard, SettingsFooter } from './settings-shell';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Számlázás — `markup.html` 1587–1710, under decision R-5.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP HAS SIX PANELS AND THIS HAS TWO, BECAUSE FOUR OF THEM ARE A PAYMENT PROVIDER'S
 * DATA.** R-5 says billing is mockup-level and nothing charges anybody — which does not make the data
 * optional, it makes most of it somebody else's:
 *
 *  * **the invoicing details are the CUSTOMER's** — the same facts whatever provider is chosen, typed
 *    by the workspace and needed the day one arrives. Built, validated, saved;
 *  * **seats USED are ours** — active members, and the mockup's own note says a deactivated one does
 *    not hold a seat. Shown as a count;
 *  * **the plan, the price, the renewal date, the card and the invoice list are the PROVIDER's.**
 *    There is no provider. `plan: 'trial'`, `card: { last4: '4242' }` and three invoice rows would be
 *    a **fabricated billing history** — the one kind of placeholder that reads as fact, and the kind
 *    somebody eventually screenshots into a finance conversation. One panel says so instead;
 *  * **seats TOTAL is the provider's too**, so there is no percentage bar. A bar against an invented
 *    denominator is a number that looks measured.
 */

export interface BillingDraft {
  company: string;
  email: string;
  address: string;
  countryCode: BillingCountry;
  vatNumber: string;
}

export function billingDraftOf(details: BillingDetails): BillingDraft {
  return {
    company: details.company,
    email: details.email,
    address: details.address,
    countryCode: asCountry(details.countryCode),
    vatNumber: details.vatNumber,
  };
}

/** ⚠️ A narrowing function rather than a cast: the column is `text`, so a stored value could be any. */
function asCountry(value: string): BillingCountry {
  return BILLING_COUNTRIES.includes(value as BillingCountry) ? (value as BillingCountry) : 'HU';
}

function countryLabel(t: Translate, code: BillingCountry): string {
  /* One `t()` per branch with a literal key. */
  if (code === 'HU') return t('billing.country.HU');
  if (code === 'DE') return t('billing.country.DE');
  if (code === 'AT') return t('billing.country.AT');
  if (code === 'RO') return t('billing.country.RO');
  if (code === 'GB') return t('billing.country.GB');
  return t('billing.country.US');
}

export interface BillingSectionProps {
  readonly details: BillingDetails;
  readonly draft: BillingDraft;
  readonly canManage: boolean;
  readonly saving: boolean;
  readonly serverError: string | null;
  readonly onDraft: (patch: Partial<BillingDraft>) => void;
  readonly onReset: () => void;
  readonly onSave: () => void;
}

export function BillingSection(props: BillingSectionProps) {
  const t = useT();
  const { details, draft, canManage } = props;

  const dirty =
    draft.company !== details.company ||
    draft.email !== details.email ||
    draft.address !== details.address ||
    draft.countryCode !== details.countryCode ||
    draft.vatNumber !== details.vatNumber;

  /* ⚠️ The shared predicates, so this and the server refuse the same values. */
  const vatOk = vatLooksValid(draft.countryCode, draft.vatNumber);
  const emailOk = billingEmailLooksValid(draft.email);

  return (
    <div data-screen="billing" className="mx-auto flex max-w-[680px] flex-col gap-3 p-5">
      {/**
       * ⚠️⚠️ **THE PANEL THAT REPLACES FOUR.** It is a statement rather than a disabled plan card,
       * because a greyed-out *"Professional · 148 000 Ft/hó"* beside a greyed-out Visa ending 4242
       * still tells somebody they are on a plan and have a card on file. Neither is true.
       */}
      <div className="bg-muted/40 flex items-start gap-3 rounded-xl border p-4">
        <Icon name="credit-card" size={16} className="text-muted-foreground mt-0.5 shrink-0" />
        <div className="min-w-0">
          <h3 className="text-[13px] font-medium">{t('billing.noProvider.title')}</h3>
          <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
            {t('billing.noProvider.body')}
          </p>
        </div>
      </div>

      {/**
       * ⚠️⚠️ **THE TRIAL BANNER THE MOCKUP DRAWS, WITH A REAL DATE.** `tenants.trial_ends_at` (0033)
       * is where it comes from, and the sweep that acts on it is real — so this is a statement about
       * something that will happen, not a marketing strip. Absent entirely for a tenant that is not
       * on a trial, rather than rendered empty.
       */}
      {details.trialEndsAt === null ? null : (
        <div className="bg-muted/40 flex items-start gap-3 rounded-xl border p-4">
          <Icon name="clock" size={16} className="text-muted-foreground mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h3 className="text-[13px] font-medium">{t('billing.trial.title')}</h3>
            <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
              {t('billing.trial.body', { date: details.trialEndsAt.slice(0, 10) })}
            </p>
          </div>
        </div>
      )}

      {/**
       * ⚠️⚠️ **THE PLAN PANEL C16 REFUSED, NOW THAT THERE IS SOMETHING TRUE TO PUT IN IT.** Its
       * objection was to fabrication — *"storing `plan: 'trial'`, a card ending 4242 and three
       * invoice rows would be a fabricated billing history"* — and a plan an operator assigned in the
       * Control Panel is not fabricated. **What is still absent is still absent:** no card, no invoice
       * list, no renewal date. R-5 is untouched; the panel above says which half is missing.
       */}
      <SettingsCard title={t('billing.plan.title')} lede={t('billing.plan.lede')}>
        {details.plan === null ? (
          <div className="mt-3 flex flex-col gap-1">
            <span className="text-[15px] font-medium">{t('billing.plan.none')}</span>
            {/* ⚠️ Said out loud, so "no plan" reads as the trial state rather than as a fault. */}
            <p className="text-muted-foreground text-[11.5px] leading-relaxed">
              {t('billing.plan.noneNote')}
            </p>
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-1">
            <span className="text-[15px] font-medium">{details.plan.name}</span>
            <span className="text-muted-foreground text-[12px]">
              {/* ⚠️ The price straight from the string the server sent. Nothing is computed from it
                  — there is no total, because there is no invoice. */}
              {t('billing.plan.perSeat', {
                price: `${details.plan.seatPrice} ${details.plan.currency}`,
              })}
            </span>
          </div>
        )}
      </SettingsCard>

      <SettingsCard title={t('billing.seats.title')} lede={t('billing.seats.lede')}>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-[22px] font-medium tabular-nums">{details.seatsUsed}</span>
          <span className="text-muted-foreground text-[12px]">{t('billing.seats.unit')}</span>
        </div>
        {/**
         * ⚠️⚠️ **THE ALLOWANCE IS KNOWN NOW, SO IT IS SHOWN — AND STILL NOT AS A BAR.** C16 drew
         * neither because the total was *"the provider's number"*; S6's plan carries it. A percentage
         * bar is still wrong: the seats in use and the seats an invitation is holding are two facts,
         * and a single bar would flatten them into one an OWNER cannot act on.
         */}
        <div className="mt-1.5 flex flex-col gap-0.5">
          <span className="text-muted-foreground text-[11.5px]">
            {details.tenantSeats.limit === null
              ? t('billing.seats.unlimited')
              : t('billing.seats.limit', { limit: details.tenantSeats.limit })}
          </span>
          {details.tenantSeats.pending === 0 ? null : (
            <span className="text-muted-foreground text-[11.5px]">
              {t('billing.seats.pending', { count: details.tenantSeats.pending })}
            </span>
          )}
          {/* ⚠️ Drawn only when the two numbers differ, which today they never do — 0031 refuses a
              second workspace. It is here so the day they can differ, the screen already says which
              is which rather than quietly relabelling one. */}
          {details.tenantSeats.used === details.seatsUsed ? null : (
            <span className="text-muted-foreground text-[11.5px]">
              {t('billing.seats.tenant', { count: details.tenantSeats.used })}
            </span>
          )}
        </div>
        <p className="text-muted-foreground mt-1.5 text-[11.5px] leading-relaxed">
          {t('billing.seats.note')}
        </p>
      </SettingsCard>

      <SettingsCard title={t('billing.details.title')} lede={t('billing.details.lede')}>
        <div className="mt-3 flex flex-col gap-3">
          <Field label={t('billing.field.company')}>
            <TextInput
              value={draft.company}
              disabled={!canManage}
              onChange={(event) => {
                props.onDraft({ company: event.target.value });
              }}
            />
          </Field>

          <Field
            label={t('billing.field.email')}
            note={emailOk ? t('billing.field.emailNote') : undefined}
            error={emailOk ? undefined : t('billing.error.email')}
          >
            <TextInput
              value={draft.email}
              disabled={!canManage}
              onChange={(event) => {
                props.onDraft({ email: event.target.value });
              }}
            />
          </Field>

          <Field label={t('billing.field.address')}>
            <TextInput
              value={draft.address}
              disabled={!canManage}
              onChange={(event) => {
                props.onDraft({ address: event.target.value });
              }}
            />
          </Field>

          <Field label={t('billing.field.country')}>
            <Select
              value={draft.countryCode}
              disabled={!canManage}
              onChange={(event) => {
                props.onDraft({ countryCode: asCountry(event.target.value) });
              }}
              options={BILLING_COUNTRIES.map((code) => ({
                value: code,
                label: countryLabel(t, code),
              }))}
            />
          </Field>

          {/**
           * ⚠️⚠️ **THE NOTE CHANGES WITH THE COUNTRY, because the format does.** Hungary wants `HU`
           * plus eight digits; anywhere else a country code plus eight to twelve characters. A single
           * "invalid VAT number" would leave somebody guessing at the shape.
           */}
          <Field
            label={t('billing.field.vat')}
            note={vatOk ? t('billing.field.vatOk') : undefined}
            error={
              vatOk
                ? undefined
                : draft.countryCode === 'HU'
                  ? t('billing.error.vatHu')
                  : t('billing.error.vatEu')
            }
          >
            <TextInput
              value={draft.vatNumber}
              disabled={!canManage}
              placeholder="HU12345678"
              onChange={(event) => {
                /* ⚠️ Upper-cased as you type, because the wire schema upper-cases it anyway — and a
                 * field that silently rewrites your input on save is a field people mistrust. */
                props.onDraft({ vatNumber: event.target.value.toUpperCase() });
              }}
            />
          </Field>
        </div>
      </SettingsCard>

      {props.serverError !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-[12.5px]"
        >
          {props.serverError}
        </p>
      )}

      {canManage && (
        <SettingsFooter
          note={dirty ? t('settings.unsaved') : t('settings.saved')}
          dirty={dirty && vatOk && emailOk}
          saving={props.saving}
          onReset={props.onReset}
          onSave={props.onSave}
        />
      )}
    </div>
  );
}
